import { Check, Home, Users, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';

const Pricing = () => {
  const plans = [
    {
      icon: Home,
      name: 'Home Visit by DigiMitra',
      price: '₹499',
      period: 'per hour',
      description: 'Personalized in-home digital training for your parents',
      features: [
        'One-on-one training session',
        'Learn at your own pace',
        'Customized to your needs',
        'Smartphone & app guidance',
        'Fraud awareness training',
        'Follow-up support included'
      ],
      cta: 'Book a Visit',
      link: '/services#digimitra',
      popular: true
    },
    {
      icon: Users,
      name: 'Organize an Elderra Training',
      price: '₹9,000',
      period: 'onwards',
      description: 'Group training sessions for communities and organizations',
      features: [
        'Group sessions (10-30 people)',
        'Customized curriculum',
        'Interactive workshops',
        'Digital literacy certification',
        'Training materials included',
        'Perfect for RWAs & senior clubs'
      ],
      cta: 'Contact Us',
      link: '/contact',
      popular: false
    },
    {
      icon: MessageCircle,
      name: 'Elderra Voice Companion',
      price: 'Coming Soon',
      period: '',
      description: 'AI-powered Voice Companion on WhatsApp for daily digital needs',
      features: [
        '24/7 availability',
        'Voice & text support',
        'Bill payments & reminders',
        'Government schemes info',
        'App guidance',
        'Emergency assistance'
      ],
      cta: 'Chat on WhatsApp',
      link: 'https://wa.me/919205070300?text=Hi%20Elderra%2C%20I%27d%20like%20to%20try%20the%20Voice%20Companion',
      external: true,
      popular: false
    }
  ];

  return (
    <div className="page-container">
      <SEO 
        title="Pricing Plans - Elderra | Digital Training for Seniors"
        description="Affordable digital literacy services for seniors. DigiMitra home visits at ₹499/hour, group training from ₹9,000, and Elderra Voice Companion on WhatsApp coming soon."
        keywords="DigiMitra pricing, elderly digital training cost, senior tech support pricing, home visit rates, group training for seniors"
        ogImage="https://customer-assets.emergentagent.com/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/4owwqeky_Elderra%20Banner.png"
        ogUrl="https://elderra.io/pricing"
      />

      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Pricing Plans</h1>
          <p className="page-subtitle">
            Choose the perfect plan to empower your parents with digital literacy
          </p>
          <p data-testid="pricing-gst-note" style={{ marginTop: 14, fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
            All prices inclusive of 18% GST · Billed by GiantTreeTechnology Pvt Ltd · GSTIN 07AAICG7122N1ZA
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-section">
        <div className="container">
          <div className="pricing-grid">
            {plans.map((plan, index) => {
              const IconComponent = plan.icon;
              return (
                <div 
                  key={index} 
                  className={`pricing-card ${plan.popular ? 'pricing-card-popular' : ''}`}
                >
                  {plan.popular && (
                    <div className="pricing-badge">Most Popular</div>
                  )}
                  <div className="pricing-icon-container">
                    <IconComponent size={48} className="pricing-icon" />
                  </div>
                  <h3 className="pricing-name">{plan.name}</h3>
                  <div className="pricing-price-container">
                    <span className="pricing-price">{plan.price}</span>
                    {plan.period && <span className="pricing-period"> {plan.period}</span>}
                  </div>
                  <p className="pricing-description">{plan.description}</p>
                  
                  <ul className="pricing-features">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="pricing-feature">
                        <Check size={20} className="pricing-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {plan.external ? (
                    <a href={plan.link} target="_blank" rel="noopener noreferrer" className="pricing-cta-link">
                      <Button 
                        className={plan.popular ? 'btn-primary-large' : 'btn-secondary-large'}
                        size="lg"
                      >
                        {plan.cta}
                      </Button>
                    </a>
                  ) : (
                    <Link to={plan.link} className="pricing-cta-link">
                      <Button 
                        className={plan.popular ? 'btn-primary-large' : 'btn-secondary-large'}
                        size="lg"
                      >
                        {plan.cta}
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Not sure which plan is right?</h2>
            <p>Contact us and we'll help you choose the best option for your needs</p>
            <Link to="/contact">
              <Button className="btn-primary-large" size="lg">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
